import { useEffect, useState, type FormEvent } from "react";
import { Brain, Menu, Pencil, Plus, Search, Trash2 } from "lucide-react";

import { api } from "../api";
import type { MemoryFact } from "../types";

type Props = {
  onOpenSidebar: () => void;
  onToast: (message: string, kind?: "success" | "error") => void;
};

export function MemoryPage({ onOpenSidebar, onToast }: Props) {
  const [facts, setFacts] = useState<MemoryFact[]>([]);
  const [search, setSearch] = useState("");
  const [newFact, setNewFact] = useState("");
  const [editing, setEditing] = useState<number | null>(null);
  const [editText, setEditText] = useState("");

  const load = async (query = search) => {
    try { setFacts(await api.memory(query)); }
    catch (error) { onToast(error instanceof Error ? error.message : "Не удалось загрузить память", "error"); }
  };

  useEffect(() => {
    void load("");
    void api.markMemorySeen();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (event: FormEvent) => {
    event.preventDefault();
    if (!newFact.trim()) return;
    try {
      await api.createMemory(newFact.trim());
      setNewFact("");
      await load();
      onToast("Факт добавлен");
    } catch (error) { onToast(error instanceof Error ? error.message : "Не удалось добавить факт", "error"); }
  };

  const save = async (id: number) => {
    if (!editText.trim()) return;
    try {
      await api.updateMemory(id, editText.trim());
      setEditing(null);
      await load();
    } catch (error) { onToast(error instanceof Error ? error.message : "Не удалось изменить факт", "error"); }
  };

  const remove = async (id: number) => {
    if (!window.confirm("Удалить этот факт из памяти?")) return;
    try { await api.deleteMemory(id); await load(); }
    catch (error) { onToast(error instanceof Error ? error.message : "Не удалось удалить факт", "error"); }
  };

  return (
    <main className="workspace-page">
      <header className="workspace-topbar">
        <button className="icon-button button-ghost mobile-only" aria-label="Открыть меню" onClick={onOpenSidebar}><Menu /></button>
        <div><p className="eyebrow">SuperGPT</p><h1>Память</h1></div>
      </header>
      <div className="workspace-content memory-content">
        <form className="search-field memory-search" onSubmit={(event) => { event.preventDefault(); void load(); }}>
          <Search /><input type="search" placeholder="Поиск по памяти" value={search} onChange={(event) => setSearch(event.target.value)} />
        </form>
        <form className="new-memory-row" onSubmit={add}>
          <input placeholder="Добавить факт" value={newFact} onChange={(event) => setNewFact(event.target.value)} />
          <button className="button button-primary" type="submit" disabled={!newFact.trim()}><Plus /> Добавить</button>
        </form>
        <section className="memory-list" aria-label="Факты памяти">
          {facts.map((fact) => (
            <article className="memory-row" key={fact.id}>
              <span className="memory-icon" aria-hidden="true"><Brain /></span>
              {editing === fact.id ? (
                <input autoFocus value={editText} onChange={(event) => setEditText(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void save(fact.id); if (event.key === "Escape") setEditing(null); }} />
              ) : <p>{fact.content}</p>}
              {fact.is_new === 1 && <span className="new-badge">новое</span>}
              <div className="row-actions">
                {editing === fact.id ? <button className="button button-glass" type="button" onClick={() => void save(fact.id)}>Сохранить</button> : <button className="icon-button button-ghost" type="button" aria-label="Изменить факт" title="Изменить" onClick={() => { setEditing(fact.id); setEditText(fact.content); }}><Pencil /></button>}
                <button className="icon-button button-ghost danger-icon" type="button" aria-label="Удалить факт" title="Удалить" onClick={() => void remove(fact.id)}><Trash2 /></button>
              </div>
            </article>
          ))}
          {!facts.length && <div className="empty-state compact"><Brain /><h2>{search ? "Ничего не найдено" : "Память пока пуста"}</h2></div>}
        </section>
      </div>
    </main>
  );
}

